import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * De schil van een beheerscherm binnen een event.
 *
 * Op mobiel een kop met een pijl terug en de eventnaam eronder (ronde 20H, 20I,
 * 20N); op desktop een titel in de display-font met een regel eronder, want daar
 * staat de zijnavigatie er al naast (ronde 20S, 20T, 20U, 20P).
 */
export function EventScreen({
  title,
  eventTitle,
  desktopSubtitle,
  aside,
  footer,
  children,
}: {
  title: string
  eventTitle: string
  desktopSubtitle: string
  aside?: ReactNode
  footer?: ReactNode
  children: ReactNode
}) {
  const navigate = useNavigate()

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="border-line-soft flex shrink-0 items-center gap-3.5 border-b px-5.5 pt-5.5 pb-4 lg:hidden">
        <button
          type="button"
          aria-label="Terug"
          onClick={() => navigate('..', { relative: 'path' })}
          className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink flex size-9.5 items-center justify-center rounded-full border text-base transition-colors"
        >
          ←
        </button>
        <div>
          <p className="text-[17px] font-semibold">{title}</p>
          <p className="text-ink-faint mt-0.5 text-[13px]">{eventTitle}</p>
        </div>
      </header>

      <div className="hidden shrink-0 items-end justify-between gap-6 px-12 pt-8.5 lg:flex">
        <div>
          <h1 className="font-display text-[40px] leading-[1.05]">{title}</h1>
          <p className="text-ink-soft mt-2 text-[15px]">{desktopSubtitle}</p>
        </div>
        {aside}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5.5 pt-5 pb-8 lg:px-12 lg:pt-7.5">
        {children}
      </div>

      {footer && (
        <div className="border-line-soft shrink-0 border-t px-5.5 pt-4 pb-7.5 lg:px-12 lg:py-5">
          {footer}
        </div>
      )}
    </div>
  )
}

/** De twee knoppen onderaan een bewerkscherm (ronde 20H, 20I, 20T, 20U). */
export function SaveBar({
  saving,
  dirty,
  onCancel,
  onSave,
}: {
  saving: boolean
  dirty: boolean
  onCancel: () => void
  onSave: () => void
}) {
  return (
    <div className="flex gap-2.5 lg:justify-end">
      <button
        type="button"
        onClick={onCancel}
        disabled={!dirty || saving}
        className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink h-13.5 shrink-0 basis-29 rounded-full border text-base transition-colors disabled:opacity-40 lg:h-12.5 lg:basis-auto lg:px-6 lg:text-[15px]"
      >
        Annuleren
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={!dirty || saving}
        className="bg-accent text-accent-ink disabled:bg-inert disabled:text-inert-ink h-13.5 flex-1 rounded-full text-base font-semibold transition hover:brightness-95 lg:h-12.5 lg:flex-none lg:px-7.5 lg:text-[15px]"
      >
        {saving ? 'Opslaan…' : 'Opslaan'}
      </button>
    </div>
  )
}
